import { useEffect, useRef, useCallback } from 'react';
import { authAPI } from '../service/api';

/**
 * Custom hook for automatic token refresh
 * Refreshes access token every 25 minutes
 */
export const useTokenRefresh = (isAuthenticated, refreshAccessToken) => {
  const intervalRef = useRef(null);
  const isRefreshingRef = useRef(false);

  // Refresh token function
  const refreshToken = useCallback(async () => {
    if (isRefreshingRef.current) {
      return; // Prevent multiple simultaneous refresh attempts
    }

    const refreshTokenValue = localStorage.getItem('my_refresh_token');
    if (!refreshTokenValue) {
      console.log('No refresh token available');
      return;
    }

    try {
      isRefreshingRef.current = true;
      console.log('🔄 Refreshing access token...');
      
      const response = await authAPI.refreshToken({ refresh_token: refreshTokenValue });
      console.log("refresh token response", response);
      
      if (response.data.status === 'success') {
        const newAccessToken = response.data.data.access_token;
        localStorage.setItem('my_access_token', newAccessToken);
        
        console.log('✅ Access token refreshed successfully');
        
        // Call the refresh function from AuthContext if provided
        if (refreshAccessToken) {
          refreshAccessToken(newAccessToken);
        }
      } else {
        console.error('❌ Token refresh failed:', response.data.message);
        // If refresh fails, clear tokens and logout
        localStorage.removeItem('my_access_token');
        localStorage.removeItem('my_refresh_token');
        localStorage.removeItem('user_data');
        window.location.href = '/login';
      }
    } catch (error) {
      console.error('❌ Token refresh error:', error);
      // If refresh fails, clear tokens and logout
      localStorage.removeItem('my_access_token');
      localStorage.removeItem('my_refresh_token');
      localStorage.removeItem('user_data');
      window.location.href = '/login';
    } finally {
      isRefreshingRef.current = false;
    }
  }, [refreshAccessToken]);

  // Set up automatic refresh timer
  useEffect(() => {
    if (isAuthenticated) {
      // Refresh every 25 minutes (1585800 ms)
      const REFRESH_INTERVAL = 25 * 60 * 1000;
      
      console.log('⏰ Setting up token refresh timer (25 minutes)');
      
      intervalRef.current = setInterval(() => {
        refreshToken();
      }, REFRESH_INTERVAL);

      // Also refresh immediately if token is close to expiring
      const accessToken = localStorage.getItem('my_access_token');
      if (accessToken) {
        try {
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          const expirationTime = payload.exp * 1000;
          const currentTime = Date.now();
          const timeUntilExpiry = expirationTime - currentTime;
          
          // If token expires in less than 5 minutes, refresh now
          if (timeUntilExpiry < 5 * 60 * 1000) {
            console.log('⚠️ Token expires soon, refreshing now...');
            refreshToken();
          }
        } catch (error) {
          console.error('Error parsing token:', error);
        }
      }
    } else {
      // Clear interval if not authenticated
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        console.log('🛑 Token refresh timer cleared');
      }
    }

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, refreshToken]);

  // Return manual refresh function
  return {
    refreshToken,
    isRefreshing: isRefreshingRef.current
  };
};

export default useTokenRefresh;
